import { api } from './client';
import type { ConsoleAdmin, LoginInput, TwoFactorVerifyInput } from '@/types';

type ApiRes<T> = { success: boolean; data: T };

interface PublicKeyCredentialCreationOptionsJSON {
  challenge: string;
  rp: { name: string; id?: string };
  user: { id: string; name: string; displayName: string };
  pubKeyCredParams: Array<{ alg: number; type: string }>;
  timeout?: number;
  excludeCredentials?: Array<{ id: string; type: string; transports?: string[] }>;
  authenticatorSelection?: Record<string, unknown>;
  attestation?: string;
  extensions?: Record<string, unknown>;
}

interface PublicKeyCredentialRequestOptionsJSON {
  challenge: string;
  timeout?: number;
  rpId?: string;
  allowCredentials?: Array<{ id: string; type: string; transports?: string[] }>;
  userVerification?: string;
  extensions?: Record<string, unknown>;
}

interface PasskeyInfo {
  id: string;
  credentialId: string;
  name: string | null;
  createdAt: string;
  lastUsedAt: string | null;
}

export const authApi = {
  login: async (input: LoginInput) => {
    const { data } = await api.post<ApiRes<{ admin: ConsoleAdmin; requiresTwoFactor: boolean; tokens?: { accessToken: string } }>>('/auth/login', input);
    return data.data;
  },
  verifyTwoFactor: async (input: TwoFactorVerifyInput) => {
    const { data } = await api.post<ApiRes<{ admin: ConsoleAdmin }>>('/auth/2fa/verify', input);
    return data.data;
  },
  getMe: async () => {
    const { data } = await api.get<ApiRes<{ admin: ConsoleAdmin }>>('/auth/me');
    return data.data.admin;
  },
  logout: async () => {
    await api.post('/auth/logout');
  },
  refresh: async () => {
    await api.post('/auth/refresh');
  },
  setupTwoFactor: async () => {
    const { data } = await api.post<ApiRes<{ secret: string; otpauthUrl: string }>>('/auth/2fa/setup');
    return data.data;
  },
  confirmTwoFactor: async (code: string) => {
    await api.post('/auth/2fa/confirm', { code });
  },

  // Passkey endpoints
  passkeyRegisterOptions: async () => {
    const { data } = await api.post<ApiRes<{ options: PublicKeyCredentialCreationOptionsJSON }>>('/auth/passkeys/register-options');
    return data.data;
  },
  passkeyRegister: async (credential: unknown) => {
    const { data } = await api.post<ApiRes<{ verified: boolean }>>('/auth/passkeys/register', { credential });
    return data.data;
  },
  passkeyAuthenticateOptions: async (email?: string) => {
    const { data } = await api.post<ApiRes<{ options: PublicKeyCredentialRequestOptionsJSON }>>('/auth/passkeys/authenticate-options', { email });
    return data.data;
  },
  passkeyAuthenticate: async (credential: unknown) => {
    const { data } = await api.post<ApiRes<{ admin: ConsoleAdmin }>>('/auth/passkeys/authenticate', { credential });
    return data.data;
  },
  listPasskeys: async () => {
    const { data } = await api.get<ApiRes<PasskeyInfo[]>>('/auth/passkeys');
    return data.data;
  },
  deletePasskey: async (id: string) => {
    await api.delete(`/auth/passkeys/${id}`);
  },
};
